import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { AlertTriangle } from 'lucide-react';
import Modal from '../Modal';
import { handleApiError } from '../../utils/errorHandler';
import {
  balancesApi,
  PartyBalanceDetail,
  formatCurrency,
  todayIso,
} from '../../services/agentBalancesService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  party: PartyBalanceDetail;
  singleClientId?: number | null;
  onSaved: () => void;
}

interface Row {
  clientId: number;
  clientName: string;
  remaining: number;
  selected: boolean;
  amount: string;
}

const parseAmount = (v: string): number => {
  const n = parseFloat(v);
  return isNaN(n) || n < 0 ? 0 : n;
};

const TableWrap = styled.div`
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  overflow: hidden;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

const Th = styled.th`
  padding: 10px 12px;
  text-align: center;
  font-weight: 600;
  font-size: 13px;
  color: var(--text-secondary);
  background: var(--background-color);
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 8px 12px;
  text-align: center;
  border-bottom: 1px solid var(--border-color-light);
  color: var(--text-primary);
`;

const ClientCell = styled(Td)`
  text-align: right;
  font-weight: 500;
`;

const AmountInput = styled.input`
  width: 120px;
  padding: 7px 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  text-align: left;
  direction: ltr;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }

  &:disabled {
    background: var(--background-color);
    color: var(--text-tertiary);
  }
`;

const AfterCell = styled(Td)<{ $negative: boolean }>`
  color: ${p => (p.$negative ? 'var(--error-color)' : 'var(--text-primary)')};
  font-weight: ${p => (p.$negative ? 700 : 400)};
  direction: ltr;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: var(--primary-color);
  cursor: pointer;
`;

const TotalBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  padding: 14px 16px;
  background: var(--primary-light);
  border-radius: var(--border-radius-sm);
`;

const TotalLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
`;

const TotalValue = styled.span`
  font-size: 22px;
  font-weight: 700;
  color: var(--primary-color);
  direction: ltr;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 18px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 160px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-secondary);
`;

const TextInput = styled.input`
  padding: 9px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const OverpayWarn = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 14px;
  padding: 10px 14px;
  background: var(--warning-light, #fff7e6);
  border: 1px solid var(--warning-color);
  border-radius: var(--border-radius-sm);
  color: var(--warning-color);
  font-size: 13px;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${p => (p.$variant === 'primary' ? 'var(--primary-color)' : 'var(--border-color)')};
  background: ${p => (p.$variant === 'primary' ? 'var(--primary-color)' : 'var(--surface-color)')};
  color: ${p => (p.$variant === 'primary' ? '#fff' : 'var(--text-primary)')};

  &:hover:not(:disabled) {
    background: ${p => (p.$variant === 'primary' ? 'var(--primary-hover)' : 'var(--hover-background)')};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const EmptyNote = styled.p`
  text-align: center;
  color: var(--text-secondary);
  padding: 24px 0;
`;

const BulkPaymentModal: React.FC<Props> = ({ isOpen, onClose, party, singleClientId, onSaved }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [paymentDate, setPaymentDate] = useState(todayIso());
  const [note, setNote] = useState('');
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);

  const isSingle = singleClientId != null;

  useEffect(() => {
    if (!isOpen) return;

    const source = isSingle
      ? party.clients.filter(c => c.clientId === singleClientId)
      : party.clients.filter(c => c.totalOwed > 0);

    const initial: Row[] = source.map(c => {
      const openBalance = c.remaining > 0 ? c.remaining : 0;
      return {
        clientId: c.clientId,
        clientName: c.clientName,
        remaining: c.remaining,
        selected: isSingle ? true : c.remaining > 0,
        amount: openBalance > 0 ? String(round2(openBalance)) : '',
      };
    });

    setRows(initial);
    setPaymentDate(todayIso());
    setNote('');
    setReference('');
    setSaving(false);
  }, [isOpen, party, singleClientId, isSingle]);

  const toggleRow = (clientId: number) => {
    setRows(prev => prev.map(r => (r.clientId === clientId ? { ...r, selected: !r.selected } : r)));
  };

  const setAmount = (clientId: number, value: string) => {
    setRows(prev => prev.map(r => (r.clientId === clientId ? { ...r, amount: value } : r)));
  };

  const selectedRows = rows.filter(r => r.selected);
  const total = useMemo(
    () => selectedRows.reduce((sum, r) => sum + parseAmount(r.amount), 0),
    [selectedRows]
  );
  const payCount = selectedRows.filter(r => parseAmount(r.amount) > 0).length;
  const anyOverpay = selectedRows.some(r => parseAmount(r.amount) > r.remaining + 0.001);

  const handleConfirm = async () => {
    const items = selectedRows
      .filter(r => parseAmount(r.amount) > 0)
      .map(r => ({ clientId: r.clientId, amount: round2(parseAmount(r.amount)) }));

    if (items.length === 0) {
      toast.error('יש לבחור לפחות לקוח אחד עם סכום גדול מאפס');
      return;
    }

    try {
      setSaving(true);
      const result = await balancesApi.createPaymentsBulk({
        partySourceId: party.sourceId,
        partyType: party.partyType,
        paymentDate,
        note: note.trim() || null,
        reference: reference.trim() || null,
        items,
      });
      toast.success(`נוצרו ${result.count} תשלומים בסך ${formatCurrency(result.total)}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(handleApiError(err, 'שגיאה ביצירת התשלומים'));
    } finally {
      setSaving(false);
    }
  };

  const title = isSingle
    ? `הוספת תשלום · ${rows[0]?.clientName ?? ''}`
    : `תשלום מרוכז · ${party.partyTypeName} ${party.sourceName}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      width="720px"
      maxWidth="95vw"
      footer={
        <>
          <Button type="button" onClick={onClose} disabled={saving}>ביטול</Button>
          <Button type="button" $variant="primary" onClick={handleConfirm} disabled={saving || payCount === 0}>
            {saving ? 'שומר...' : `צור ${payCount} תשלומים`}
          </Button>
        </>
      }
    >
      {rows.length === 0 ? (
        <EmptyNote>אין לצד זה לקוחות עם חוב פתוח לתשלום.</EmptyNote>
      ) : (
        <>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  {!isSingle && <Th style={{ width: 40 }} />}
                  <Th style={{ textAlign: 'right' }}>לקוח</Th>
                  <Th>חוב נוכחי</Th>
                  <Th>סכום לתשלום</Th>
                  <Th>יתרה אחרי</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const amount = parseAmount(r.amount);
                  const after = r.remaining - amount;
                  return (
                    <tr key={r.clientId}>
                      {!isSingle && (
                        <Td>
                          <Checkbox
                            type="checkbox"
                            checked={r.selected}
                            onChange={() => toggleRow(r.clientId)}
                          />
                        </Td>
                      )}
                      <ClientCell>{r.clientName}</ClientCell>
                      <Td style={{ direction: 'ltr' }}>{formatCurrency(r.remaining)}</Td>
                      <Td>
                        <AmountInput
                          type="number"
                          min="0"
                          step="0.01"
                          value={r.amount}
                          disabled={!r.selected}
                          onChange={e => setAmount(r.clientId, e.target.value)}
                        />
                      </Td>
                      <AfterCell $negative={after < -0.001}>{formatCurrency(after)}</AfterCell>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableWrap>

          {anyOverpay && (
            <OverpayWarn>
              <AlertTriangle size={16} />
              סכום התשלום עולה על החוב הנוכחי של לקוח אחד או יותר (יתרה שלילית). ניתן להמשיך.
            </OverpayWarn>
          )}

          <TotalBar>
            <TotalLabel>סה"כ לתשלום</TotalLabel>
            <TotalValue>{formatCurrency(total)}</TotalValue>
          </TotalBar>

          <MetaRow>
            <Field>
              <FieldLabel>תאריך תשלום</FieldLabel>
              <TextInput type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} />
            </Field>
            <Field>
              <FieldLabel>אסמכתא (אופציונלי)</FieldLabel>
              <TextInput type="text" value={reference} maxLength={100} onChange={e => setReference(e.target.value)} placeholder="מס' העברה / צ'ק" />
            </Field>
            <Field>
              <FieldLabel>הערה (אופציונלי)</FieldLabel>
              <TextInput type="text" value={note} maxLength={500} onChange={e => setNote(e.target.value)} />
            </Field>
          </MetaRow>
        </>
      )}
    </Modal>
  );
};

const round2 = (n: number): number => Math.round(n * 100) / 100;

export default BulkPaymentModal;
